const aborted = (signal: AbortSignal) => signal.reason instanceof Error ? signal.reason : new DOMException('音质切换已取消', 'AbortError');

/** Bound a resolver/play promise even when its provider ignores cancellation. */
export function awaitMediaOperation<T>(operation: Promise<T>, signal: AbortSignal, timeoutMs = 15000): Promise<T> {
  if (signal.aborted) return Promise.reject(aborted(signal));
  return new Promise((resolve, reject) => {
    const finish = (error: unknown, value?: T) => {
      clearTimeout(timer); signal.removeEventListener('abort', onAbort);
      if (error) reject(error); else resolve(value as T);
    };
    const onAbort = () => finish(aborted(signal));
    const timer = setTimeout(() => finish(new Error('音质切换准备超时')), timeoutMs);
    signal.addEventListener('abort', onAbort, { once: true });
    operation.then(value => finish(null, value), error => finish(error));
  });
}

/** Wait for decoded data at the latest requested position; this never plays media. */
export function waitForMediaPosition(media: HTMLAudioElement, position: () => number, signal: AbortSignal, timeoutMs = 15000, followPosition = true, positionRevision?: () => number): Promise<void> {
  if (signal.aborted) return Promise.reject(aborted(signal));
  return new Promise((resolve, reject) => {
    let settled = false;
    const events = ['loadedmetadata', 'loadeddata', 'canplay', 'seeked', 'durationchange'];
    const finish = (error?: Error) => {
      if (settled) return;
      settled = true; clearTimeout(timer); clearInterval(poll);
      for (const event of events) media.removeEventListener(event, check);
      media.removeEventListener('error', failed); signal.removeEventListener('abort', onAbort);
      if (error) reject(error); else resolve();
    };
    let latchedTarget: number | null = null;
    let revision = positionRevision?.();
    const check = () => {
      if (media.error) { failed(); return; }
      if (media.readyState < 1) return;
      const latestRevision = positionRevision?.();
      if (latestRevision !== revision) { revision = latestRevision; latchedTarget = null; }
      const requested = position();
      const selected = followPosition ? requested : (latchedTarget ?? requested);
      if (Number.isFinite(media.duration) && Number.isFinite(selected) && selected > media.duration + 0.15) {
        finish(new Error('目标音频无法定位到当前播放位置，已保留原音频')); return;
      }
      const target = Math.max(0, Math.min(Number.isFinite(selected) ? selected : 0, Number.isFinite(media.duration) ? Math.max(0, media.duration - 0.01) : Infinity));
      const decoded = media.readyState >= 3 || media.readyState >= 2 && Number.isFinite(media.duration) && target >= media.duration - 0.15;
      if (!followPosition && decoded && latchedTarget === null) latchedTarget = target;
      const settledTarget = followPosition ? target : (latchedTarget ?? target);
      // Follow an explicit seek revision while allowing the primary Audio's
      // normal advancing clock to keep playing during a detached seek.
      if (Math.abs(media.currentTime - settledTarget) > 0.15) {
        if (!media.seeking) { try { media.currentTime = settledTarget; } catch { /* Retry after more metadata. */ } }
        return;
      }
      if (!media.seeking && decoded) finish();
    };
    const failed = () => finish(new Error(`媒体错误 ${media.error?.code || 0}: ${media.error?.message || '目标音质无法载入或解码'}`));
    const onAbort = () => finish(aborted(signal));
    const timer = setTimeout(() => finish(new Error('目标音质媒体准备超时')), timeoutMs);
    const poll = setInterval(check, 50);
    for (const event of events) media.addEventListener(event, check);
    media.addEventListener('error', failed); signal.addEventListener('abort', onAbort, { once: true });
    check();
  });
}
